import React from 'react';

// Derive the 3-way visibility mode from a saved record (handles legacy records that
// only have the old for_all_brands boolean).
export const deriveVisibilityMode = (record) => {
  if (record && record.visibility_mode) return record.visibility_mode;
  if (record && record.for_all_brands === false) return 'include';
  return 'all';
};

// Shared All / Show-for-specific / Hide-for-specific control used by the Resources and
// Announcements admin editors. `keyPrefix` must be unique per form so radio names and
// checkbox ids don't collide.
const BrandVisibilityControl = ({
  keyPrefix,
  mode,
  onModeChange,
  availableBrands,
  selectedDomains,
  onToggle,
  disabled = false,
  entityNoun = 'this',
}) => {
  const radioName = `${keyPrefix}BrandVisibility`;
  return (
    <div className="mb-3">
      <label className="form-label">Brand Visibility</label>

      <div className="form-check mb-1">
        <input
          type="radio"
          className="form-check-input"
          id={`${keyPrefix}AllBrands`}
          name={radioName}
          checked={mode === 'all'}
          onChange={() => onModeChange('all')}
          disabled={disabled}
        />
        <label className="form-check-label" htmlFor={`${keyPrefix}AllBrands`}>
          <strong>All Brands</strong> - Visible to all clients
        </label>
      </div>

      <div className="form-check mb-1">
        <input
          type="radio"
          className="form-check-input"
          id={`${keyPrefix}IncludeBrands`}
          name={radioName}
          checked={mode === 'include'}
          onChange={() => onModeChange('include')}
          disabled={disabled}
        />
        <label className="form-check-label" htmlFor={`${keyPrefix}IncludeBrands`}>
          <strong>Show for specific brands</strong> - Only the selected brands can see {entityNoun}
        </label>
      </div>

      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          id={`${keyPrefix}ExcludeBrands`}
          name={radioName}
          checked={mode === 'exclude'}
          onChange={() => onModeChange('exclude')}
          disabled={disabled}
        />
        <label className="form-check-label" htmlFor={`${keyPrefix}ExcludeBrands`}>
          <strong>Hide for specific brands</strong> - Visible to everyone except the selected brands
        </label>
      </div>

      {mode !== 'all' && (
        <div className="mt-2 ms-4 p-2 border rounded bg-light">
          <p className="text-muted small mb-2">
            {mode === 'include'
              ? `Tick the brands that should see ${entityNoun}.`
              : `Tick the brands that should NOT see ${entityNoun}. Everyone else sees it automatically.`}
          </p>
          {availableBrands.length === 0 ? (
            <p className="text-muted mb-0 small">No brands configured. Add brands in Domain Management.</p>
          ) : (
            <div className="row">
              {availableBrands.map((domain) => (
                <div className="col-md-6 col-lg-4" key={domain}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`${keyPrefix}-brand-${domain}`}
                      checked={selectedDomains.includes(domain)}
                      onChange={() => onToggle(domain)}
                      disabled={disabled}
                    />
                    <label className="form-check-label" htmlFor={`${keyPrefix}-brand-${domain}`}>
                      {domain}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
          {mode === 'include' && selectedDomains.length === 0 && availableBrands.length > 0 && (
            <small className="text-danger">Please select at least one brand to show this to</small>
          )}
          {mode === 'exclude' && selectedDomains.length === 0 && availableBrands.length > 0 && (
            <small className="text-warning">No brands hidden yet - this is currently visible to everyone.</small>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandVisibilityControl;
